import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { css } from '@emotion/react';
import {
  addYears,
  differenceInMonths,
  format,
  isAfter,
  isBefore,
  isSameMonth,
  Locale,
  startOfMonth,
  subYears,
} from 'date-fns';
import { CaptionProps, useDayPicker, useNavigation } from 'react-day-picker';
import { Stack } from '../stack';
import { caption_arrow_button_css, caption_text_button_css } from './styles';
import { semantic_colors, text_styles } from '../../foundation';
import {
  IconArrowChevronDoubleLeftBold,
  IconArrowChevronDoubleRightBold,
  IconArrowChevronLeftBold,
  IconArrowChevronRightBold,
} from '../icons/generated';
import { useDatePickerAttributes } from './hooks/useDatePickerAttributes';
import { buildCSSWithLength } from '../../styles';
import { TextButton } from '../button';
import { CaptionLabelType, DisplayMode } from './types';

export const captionColor = (disabled: boolean) =>
  disabled ? semantic_colors.content.disabled : semantic_colors.content.primary;

export interface DatePickerCaptionProps extends CaptionProps {
  /** @default false */
  multipleMonths?: boolean;
  className?: string;
  currentMonth?: Date;
  locale: Locale;
  displayMode: DisplayMode;
  onClickCaptionLabel?: (type: CaptionLabelType) => void;
}

export const Caption = ({
  multipleMonths = false,
  currentMonth: controlledMonth,
  displayMode,
  className,
  locale,
  onClickCaptionLabel,
  ...props
}: DatePickerCaptionProps) => {
  const { fromDate, toDate } = useDayPicker();
  const { goToMonth, nextMonth, previousMonth, currentMonth } = useNavigation();
  const {
    cellSize,
    caption: {
      kind,
      arrow: { size: arrowSize, width: arrowWidth, height: arrowHeight },
      style,
    },
  } = useDatePickerAttributes(displayMode);
  const arrowButtonStyles = [
    caption_arrow_button_css,
    css`
      ${buildCSSWithLength('height', arrowHeight)};
      ${buildCSSWithLength('width', arrowWidth)};
      ${buildCSSWithLength('line-height', arrowHeight / 2)};
    `,
  ];
  const textButtonStyles = [
    caption_text_button_css,
    css`
      ${text_styles[kind]}
    `,
  ];

  useEffect(() => {
    // @ts-expect-error: TS2345
    const startMonthOfControlled = startOfMonth(controlledMonth);
    // @ts-expect-error: TS2345
    if (!isSameMonth(currentMonth, controlledMonth)) {
      const controlledNextMonth = isAfter(startMonthOfControlled, currentMonth);
      const controlledPrevMonth = isBefore(startMonthOfControlled, currentMonth);
      // @ts-expect-error: TS2345
      controlledNextMonth && goToMonth(nextMonth);
      // @ts-expect-error: TS2345
      controlledPrevMonth && goToMonth(previousMonth);
    }
  }, [controlledMonth]);

  const { previousYear, nextYear } = useMemo(() => {
    const previousYear = subYears(currentMonth, 1);
    const nextYear = addYears(currentMonth, 1);

    return {
      previousYear: fromDate
        ? differenceInMonths(previousYear, fromDate) >= 0
          ? previousYear
          : undefined
        : previousYear,
      nextYear: toDate ? (isBefore(nextYear, toDate) ? nextYear : undefined) : nextYear,
    };
  }, [currentMonth]);

  return (
    <Stack
      direction='row'
      align='center'
      className={classNames('pds-date-picker-caption', className)}
      css={[
        css`
          position: relative;
          ${displayMode === 'bottomSheet' && buildCSSWithLength('width', cellSize * 7)}
        `,
        style,
      ]}
    >
      <Stack direction='row' ml='auto' className={classNames({ 'multiple-months': multipleMonths })}>
        <button
          disabled={!previousYear}
          className={classNames('previous-year-button', { 'prev-multiple-months': multipleMonths })}
          onClick={() => previousYear && goToMonth(previousYear)}
          css={arrowButtonStyles}
        >
          <IconArrowChevronDoubleLeftBold size={arrowSize} color='currentColor' />
        </button>
        <button
          disabled={!previousMonth}
          className={classNames('previous-month-button', { 'prev-multiple-months': multipleMonths })}
          onClick={() => previousMonth && goToMonth(previousMonth)}
          css={arrowButtonStyles}
        >
          <IconArrowChevronLeftBold size={arrowSize} color='currentColor' />
        </button>
      </Stack>
      <Stack
        direction='row'
        width='100%'
        align='center'
        gap={4}
        style={{ justifyContent: 'center', flexDirection: locale?.code === 'ko' ? 'row' : 'row-reverse' }}
      >
        <TextButton css={textButtonStyles} color={captionColor(false)} onClick={() => onClickCaptionLabel?.('year')}>
          {locale?.code === 'ko'
            ? format(props.displayMonth, 'PPP', { locale }).slice(0, 5)
            : format(props.displayMonth, 'yyyy', { locale })}
        </TextButton>
        <TextButton css={textButtonStyles} color={captionColor(false)} onClick={() => onClickCaptionLabel?.('month')}>
          {locale?.code === 'ko'
            ? format(props.displayMonth, 'PPP', { locale }).slice(6, 9)
            : format(props.displayMonth, 'MMM', { locale })}
        </TextButton>
      </Stack>
      <Stack direction='row' ml='auto' className={classNames({ 'multiple-months': multipleMonths })}>
        <button
          disabled={!nextMonth}
          className={classNames('next-month-button', { 'next-multiple-months': multipleMonths })}
          onClick={() => nextMonth && goToMonth(nextMonth)}
          css={arrowButtonStyles}
        >
          <IconArrowChevronRightBold size={arrowSize} color='currentColor' />
        </button>
        <button
          disabled={!nextYear}
          className={classNames('next-year-button', { 'next-multiple-months': multipleMonths })}
          onClick={() => nextYear && goToMonth(nextYear)}
          css={arrowButtonStyles}
        >
          <IconArrowChevronDoubleRightBold size={arrowSize} color='currentColor' />
        </button>
      </Stack>
    </Stack>
  );
};
